import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsSection, StatusText } from "../app/SettingsSection";
import { useSession } from "../app/session";
import { Button, Checkbox, Chip, Field, Input, Select, ConfirmButton } from "../ui";

const COLORS = ["accent", "ok", "warn", "err", "neutral"] as const;
const KINDS = ["person", "kid", "viewer"] as const;

interface Device {
  id: string;
  name: string;
  mode: "shared" | "personal" | null;
  lent: boolean;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface Person {
  id: string;
  name: string;
  color: string | null;
  kind: string;
  isAdmin: boolean;
  hasPin: boolean;
  hasPassword: boolean;
  sharesPlace?: boolean;
  pausedAt: string | null;
  devices: Device[];
  tokens: { id: string; name: string; scopes: string[] }[];
  spaces: { id: string; name: string; role: string }[];
}

/** Sent after any change to the circle's profiles, so other sections (Circle → add someone) refresh. */
export const PEOPLE_CHANGED = "drang:people-changed";

async function send(method: string, path: string, body: unknown): Promise<string | null> {
  const r = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  window.dispatchEvent(new Event(PEOPLE_CHANGED));
  if (r.ok) return null;
  return ((await r.json().catch(() => ({}))) as { error?: string }).error ?? "generic";
}

function useErrorText() {
  const { t } = useTranslation();
  return (code: string | null) => (code ? t(`errors.${code}`, { defaultValue: t("error.generic") }) : "");
}

const formKey = (p: Person) => [p.id, p.name, p.color, p.kind, p.isAdmin].join("|");

function ColorSelect({ value, onChange }: { value: string | null; onChange(v: string | null): void }) {
  const { t } = useTranslation();
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      aria-label={t("people.color")}
      data-testid="person-color"
    >
      <option value="">{t("people.colorNone")}</option>
      {COLORS.map((c) => (
        <option key={c} value={c}>
          {t(`colors.${c}`)}
        </option>
      ))}
    </Select>
  );
}

/** Set, change or remove a PIN. `needsCurrent`: a person changing their own PIN types the old one first. */
function PinForm({
  id,
  hasPin,
  needsCurrent,
  onDone,
}: {
  id: string;
  hasPin: boolean;
  needsCurrent: boolean;
  onDone(): void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [pin, setPin] = useState("");
  const [current, setCurrent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = async (next: string) => {
    const e = await send("PATCH", "/api/internal/circle/people", { id, pin: next, currentPin: current });
    setError(e);
    if (!e) {
      setPin("");
      setCurrent("");
      onDone();
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-end gap-2">
        {needsCurrent && hasPin && (
          <Input
            className="w-32"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            aria-label={t("people.currentPin")}
            placeholder={t("people.currentPin")}
            data-testid="person-current-pin"
          />
        )}
        <Input
          className="w-32"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          aria-label={hasPin ? t("people.newPin") : t("people.setPin")}
          placeholder={hasPin ? t("people.newPin") : t("people.setPin")}
          data-testid="person-pin"
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!pin}
          onClick={() => void save(pin)}
          data-testid="person-pin-save"
        >
          {hasPin ? t("people.changePin") : t("people.savePin")}
        </Button>
        {hasPin && (
          <Button variant="secondary" size="sm" onClick={() => void save("")} data-testid="person-pin-remove">
            {t("people.removePin")}
          </Button>
        )}
      </div>
      <p className="text-[12px] text-neutral-500">{hasPin ? t("people.pinIsSet") : t("people.noPin")}</p>
      {error && (
        <p className="text-[12.5px] text-err" role="alert">
          {errorText(error)}
        </p>
      )}
    </div>
  );
}

/** Name and colour of one profile; admins also choose kind and admin rights. Keyed by `formKey`, so a saved
 * change starts a fresh form. */
function ProfileForm({ person, admin, onDone }: { person: Person; admin: boolean; onDone(): void }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [name, setName] = useState(person.name);
  const [color, setColor] = useState(person.color);
  const [kind, setKind] = useState(person.kind);
  const [isAdmin, setIsAdmin] = useState(person.isAdmin);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const changed =
    name !== person.name ||
    color !== person.color ||
    (admin && (kind !== person.kind || isAdmin !== person.isAdmin));
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        const body: Record<string, unknown> = { id: person.id, name, color };
        if (admin) Object.assign(body, { kind, isAdmin: kind === "person" && isAdmin });
        const err = await send("PATCH", "/api/internal/circle/people", body);
        setError(err);
        setSaved(!err);
        if (!err) onDone();
      }}
    >
      <div className="flex flex-wrap items-end gap-2">
        <Field label={t("people.name")}>
          <Input
            className="w-48"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
            data-testid="person-name"
          />
        </Field>
        <Field label={t("people.color")}>
          <ColorSelect
            value={color}
            onChange={(v) => {
              setColor(v);
              setSaved(false);
            }}
          />
        </Field>
        {admin && (
          <Field label={t("circle.kindLabel")}>
            <Select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value);
                setSaved(false);
              }}
              data-testid="person-kind"
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`circle.kind.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      {admin && kind === "person" && (
        <label className="flex items-center gap-2 text-[13px]">
          <Checkbox
            checked={isAdmin}
            onChange={(v) => {
              setIsAdmin(v);
              setSaved(false);
            }}
            label={t("people.isAdmin")}
          />
          {t("people.isAdmin")}
          <span className="text-[12px] text-neutral-500">{t("people.isAdminHint")}</span>
        </label>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" variant="secondary" size="sm" disabled={!changed} data-testid="person-save">
          {t("people.save")}
        </Button>
        {saved && <StatusText tone="ok">{t("people.saved")}</StatusText>}
      </div>
      {error && (
        <p className="text-[12.5px] text-err" role="alert">
          {errorText(error)}
        </p>
      )}
    </form>
  );
}

function PersonRow({ person, me, onChange }: { person: Person; me: string; onChange(): void }) {
  const { t, i18n } = useTranslation();
  const errorText = useErrorText();
  const thisDevice = useSession((s) => s.session?.principal?.id);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const self = person.id === me;
  const act = async (method: string, path: string, body: unknown) => {
    const err = await send(method, path, body);
    setError(err);
    onChange();
  };
  return (
    <li className="py-2" data-testid="person-row" data-person={person.name}>
      <button
        type="button"
        className="flex w-full flex-wrap items-center gap-2 text-left"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        data-testid="person-open"
      >
        <Chip tone={person.color}>{person.name}</Chip>
        <span className="text-[12.5px] text-neutral-500">
          {t(`circle.kind.${person.kind}`)}
          {person.isAdmin && ` · ${t("people.admin")}`}
          {" · "}
          {person.hasPin ? t("people.withPin") : t("people.withoutPin")}
          {" · "}
          {t("people.deviceCount", { count: person.devices.length })}
          {person.sharesPlace && ` · ${t("people.sharesPlace")}`}
        </span>
        {person.pausedAt && (
          <StatusText tone="warn" testId="person-paused">
            {t("people.paused")}
          </StatusText>
        )}
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-4 border-l-2 border-neutral-200 pl-3 dark:border-neutral-800">
          {self ? (
            // the admin's own name, colour and PIN are in My profile: no second form here (UI cleanup U5)
            <a
              href="#settings-my-profile"
              className="text-[12.5px] text-accent underline"
              data-testid="person-self"
            >
              {t("people.selfInMyProfile")}
            </a>
          ) : (
            <>
              <ProfileForm key={formKey(person)} person={person} admin onDone={onChange} />
              <PinForm id={person.id} hasPin={person.hasPin} needsCurrent={false} onDone={onChange} />
            </>
          )}

          <div data-testid="person-devices">
            <h4 className="mb-1 text-[12.5px] text-neutral-500">{t("people.devices")}</h4>
            {person.devices.length === 0 ? (
              <p className="text-[12.5px] text-neutral-500">{t("people.noDevices")}</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {person.devices.map((d) => (
                  <li
                    key={d.id}
                    className="flex flex-wrap items-center gap-2 text-[13px]"
                    data-testid="device-row"
                  >
                    <span>{d.name}</span>
                    {d.id === thisDevice && <StatusText tone="quiet">{t("people.thisDevice")}</StatusText>}
                    {d.mode && (
                      <StatusText tone="quiet">
                        {d.lent ? t("people.deviceLent") : t(`people.device.${d.mode}`)}
                      </StatusText>
                    )}
                    <span className="text-[11.5px] text-neutral-500">
                      {d.lastSeenAt
                        ? t("people.lastSeen", { when: fmt.format(new Date(d.lastSeenAt)) })
                        : t("people.neverSeen")}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="ml-auto"
                      aria-label={t("people.signOutLabel", { name: d.name })}
                      onClick={() => void act("DELETE", "/api/internal/circle/people/device", { id: d.id })}
                      data-testid="device-sign-out"
                    >
                      {t("people.signOut")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {person.tokens.length > 0 && (
            <div>
              <h4 className="mb-1 text-[12.5px] text-neutral-500">{t("people.tokens")}</h4>
              <ul className="flex flex-col gap-0.5 text-[12.5px]">
                {person.tokens.map((k) => (
                  <li key={k.id}>
                    {k.name} <span className="text-neutral-500">· {k.scopes.join(", ")}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[12px] text-neutral-500">{t("people.tokensHint")}</p>
            </div>
          )}

          <div>
            <h4 className="mb-1 text-[12.5px] text-neutral-500">{t("circle.sharedSpaces")}</h4>
            {person.spaces.length === 0 ? (
              <p className="text-[12.5px] text-neutral-500">{t("people.noSpaces")}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="person-spaces">
                {person.spaces.map((s) => (
                  <Chip key={s.id}>
                    {s.name} · {t(`circle.role.${s.role}`)}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          {!self && (
            <div className="flex flex-col gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void act("POST", "/api/internal/circle/people/pause", {
                      id: person.id,
                      paused: !person.pausedAt,
                    })
                  }
                  data-testid="person-pause"
                >
                  {person.pausedAt ? t("people.resume") : t("people.pause")}
                </Button>
                <ConfirmButton
                  variant="danger"
                  size="sm"
                  question={t("people.removeConfirm", { name: person.name })}
                  onConfirm={() => void act("DELETE", "/api/internal/circle/profiles", { id: person.id })}
                  testId="person-remove"
                >
                  {t("people.remove")}
                </ConfirmButton>
              </div>
              <p className="text-[12px] text-neutral-500">{t("people.pauseHint")}</p>
              <p className="text-[12px] text-neutral-500">{t("people.removeHint")}</p>
            </div>
          )}
          {error && (
            <p className="text-[12.5px] text-err" role="alert">
              {errorText(error)}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Everyone in the circle (P3, circle mode), for admins — names, colours, kinds, admin rights, PINs, pausing and
 * removing, signed-in devices and shared spaces. Part of Settings → People (UI cleanup U5: People and Circle are
 * one section). Everyone else, and the admin for themselves, use *My profile*.
 */
export function PeopleList() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const me = session?.profile;
  const [people, setPeople] = useState<Person[]>([]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("person");
  const refresh = () =>
    fetch("/api/internal/circle/people", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { people: [] }))
      .then((b: { people: Person[] }) => setPeople(b.people));
  useEffect(() => {
    if (session?.mode === "circle" && me?.isAdmin) void refresh();
  }, [session?.mode, me?.isAdmin]);
  if (session?.mode !== "circle" || !me?.isAdmin) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="people-list">
      <h3 className="text-[12.5px] text-neutral-500">{t("people.everyone")}</h3>
      <p className="text-[12.5px] text-neutral-500">{t("people.hint")}</p>
      <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
        {people.map((p) => (
          <PersonRow key={p.id} person={p} me={me.id} onChange={() => void refresh()} />
        ))}
      </ul>
      <form
        className="flex flex-wrap items-center gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          await send("POST", "/api/internal/circle/profiles", { name: name.trim(), kind });
          setName("");
          void refresh();
        }}
      >
        <Input
          className="w-48"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("circle.newProfile")}
          aria-label={t("circle.newProfile")}
          data-testid="profile-name"
        />
        <Select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          aria-label={t("circle.kindLabel")}
          data-testid="profile-kind"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`circle.kind.${k}`)}
            </option>
          ))}
        </Select>
        <Button type="submit" variant="secondary" size="sm" data-testid="profile-create">
          {t("people.add")}
        </Button>
      </form>
    </div>
  );
}

/** Settings → My profile (circle mode): every person changes their own name, colour and PIN. */
export function MyProfileSettings() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const load = useSession((s) => s.load);
  const me = session?.profile;
  const [hasPin, setHasPin] = useState<boolean | null>(null);
  const refreshPin = () =>
    fetch("/api/internal/circle/profiles", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { profiles: [] }))
      .then((b: { profiles: { id: string; hasPin: boolean }[] }) =>
        setHasPin(b.profiles.find((p) => p.id === me?.id)?.hasPin ?? false),
      );
  useEffect(() => {
    if (session?.mode === "circle" && me) void refreshPin();
  }, [session?.mode, me?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (session?.mode !== "circle" || !me || me.kind === "viewer" || hasPin === null) return null;
  const person: Person = {
    id: me.id,
    name: me.name,
    color: me.color,
    kind: me.kind,
    isAdmin: me.isAdmin,
    hasPin,
    hasPassword: false,
    pausedAt: null,
    devices: [],
    tokens: [],
    spaces: [],
  };
  return (
    <SettingsSection
      id="settings-my-profile"
      intro={t("settings.intro.myProfile")}
      title={t("people.myProfile")}
      testId="settings-my-profile"
    >
      <ProfileForm key={formKey(person)} person={person} admin={false} onDone={() => void load()} />
      <PinForm id={me.id} hasPin={hasPin} needsCurrent={!me.isAdmin} onDone={() => void refreshPin()} />
    </SettingsSection>
  );
}
